import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Select as AntSelect } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Button, Select } from '../../../custom';
import Input from '../../../custom/input/input';
import { createUpdatePermission } from '../request';
import { StatusOptions } from '../../../requests';

const featureOptions = [
  // User Management
  { label: 'Applicants', path: '/applicants' },
  { label: 'Admin Users', path: '/admin-users' },
  { label: 'Admin Roles', path: '/admin-roles' },
  { label: 'Permissions', path: '/permissions' },
  { label: 'Privileges', path: '/privileges' },
  { label: 'Email Templates', path: '/email-templates' },
  { label: 'Contact Us', path: '/contact-us' },
  { label: 'School ID Config', path: '/school-id-config' },
  // Setup - Academic
  { label: 'Faculty Setup', path: '/faculty-setup' },
  { label: 'Department Setup', path: '/department-setup' },
  { label: 'Program Setup', path: '/program-setup' },
  { label: 'Program Type Setup', path: '/program-type-setup' },
  { label: 'Specialization Setup', path: '/specialization-setup' },
  { label: 'Curriculum Setup', path: '/curriculum-setup' },
  { label: 'Level Setup', path: '/level-setup' },
  { label: 'Subject Setup', path: '/subject-setup' },
  { label: 'Qualification Type Setup', path: '/qualification-type-setup' },
  // Setup - Fees & Sessions
  { label: 'Session Setup', path: '/session-setup' },
  { label: 'Mode of Study Setup', path: '/mode-of-study-setup' },
  { label: 'Application Setup', path: '/application-setup' },
  { label: 'Application Batch', path: '/application-batch' },
  { label: 'Tuition Fee Setup', path: '/tuition-fee-setup' },
  { label: 'Tuition Years Setup', path: '/tuition-years-setup' },
  { label: 'Discount Fee', path: '/discount-fee' },
  { label: 'Acceptance Fee', path: '/acceptance-fee' },
  // Setup - Admission
  { label: 'Admission Approval Stages', path: '/admission-approval-stages' },
  { label: 'Required Document', path: '/required-document' },
  { label: 'Setup References', path: '/setup-references' },
  { label: 'Tab Setup', path: '/tab-setup' },
  { label: 'Tab Display', path: '/tab-display' },
  // Setup - Bio Data
  { label: 'Country Setup', path: '/country-setup' },
  { label: 'State Setup', path: '/state-setup' },
  { label: 'LGA Setup', path: '/lga-setup' },
  { label: 'Gender Setup', path: '/gender-setup' },
  { label: 'Title Setup', path: '/title-setup' },
  { label: 'Marital Status Setup', path: '/marital-status-setup' },
  { label: 'Disability Setup', path: '/disability-setup' },
  { label: 'Religion Setup', path: '/religion-setup' },
  // Setup - School Info
  { label: 'Institution', path: '/institution' },
  { label: 'About Us', path: '/about-us' },
  { label: 'History', path: '/history' },
  { label: 'FAQ', path: '/faq' },
  { label: 'News & Events', path: '/news-and-events' },
  { label: 'School Info', path: '/school-info' },
  { label: 'Social Media', path: '/social-media' },
  { label: 'Why Us', path: '/why-us' },
  { label: 'Student Life', path: '/student-life' },
  { label: 'School Management', path: '/school-management' },
  { label: 'Form Setup', path: '/form-setup' },
  { label: 'Testimonial Setup', path: '/testimonial-setup' },
  { label: 'Accreditation Setup', path: '/accreditation-setup' },
  // Setup - Config
  { label: 'School Feature Config', path: '/school-feature-config' },
  { label: 'Matric Format', path: '/matric-format' },
  { label: 'Scholarship Setup', path: '/scholarship-setup' },
  { label: 'Scholarship Config', path: '/scholarship-config' },
  { label: 'Scholarship Application', path: '/scholarship-application' },
  { label: 'Scholarship Approval Stages', path: '/scholarship-approval-stages' },
  { label: 'Scholarship Percentage Config', path: '/scholarship-percentage-config' },
  // Reports
  { label: 'Payment Report', path: '/payment-report' },
  { label: 'Applicant Report', path: '/applicant-report' },
  { label: 'Scholarship Report', path: '/scholarship-report' },
  { label: 'Reset Users Password', path: '/reset-admin-password' },
  { label: 'Print Admission Letter', path: '/print-admission-letter' },
  { label: 'Requery Pending Payment', path: '/payment-requery' },
  { label: 'Look up SetUp', path: '/school-id-config' },
  { label: 'Applicants By Program', path: '/applicants-by-program' },





];

interface Props {
  permission: Permission;
  handleClose: () => void;
  refetch: () => void;
}

const EditPermission = ({ permission, handleClose, refetch }: Props) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const validate = Yup.object().shape({
    name: Yup.string().required('Permission name is required'),
    pathName: Yup.string().required('Feature is required'),
    status: Yup.string().required('Status is required'),
  });

  const updatePermissionMutation = useMutation({
    mutationFn: createUpdatePermission,
  });

  const updatePermissionHandler = async (values: FormikValues) => {
    const payload: UpdatePermissionPayload = {
      id: permission.id,
      name: values.name,
      isActive: values.status === 'true',
      pathName: values.pathName,
    };

    try {
      await updatePermissionMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: 'Success',
            description: data?.message || 'Permission updated successfully',
          });

          queryClient.refetchQueries({ queryKey: ['get-all-permissions'] });
          refetch();

          handleClose();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to update Permission',
      });
    }
  };

  return (
    <Formik
      initialValues={{
        name: permission.name,
        pathName: permission.pathName || '',
        status: String(permission.isActive),
      }}
      onSubmit={(values) => updatePermissionHandler(values)}
      validationSchema={validate}
      enableReinitialize={true}
    >
      {({ setFieldValue, values, errors, touched }) => (
        <Form className="fields">
          <Input label="Permission Name" placeholder="Input Permission Name" name="name" />

          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Feature</label>
            <AntSelect
              placeholder="Select Feature"
              size="large"
              style={{ width: '100%' }}
              options={featureOptions.map((f) => ({ label: f.label, value: f.path }))}
              onChange={(path) => setFieldValue('pathName', path)}
              value={values.pathName || undefined}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            />
            {touched.pathName && errors.pathName && (
              <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{errors.pathName as string}</div>
            )}
          </div>

          <Input
            label="Path Name"
            placeholder="Auto-filled from feature selection"
            name="pathName"
            disabled
          />

          <Select
            name="status"
            placeholder="Select Status"
            label="Status"
            options={
              <>
                {StatusOptions.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </>
            }
          />

          <div className="btn-group">
            <Button onClick={handleClose} variant="text" text="Cancel" />
            <Button
              text={updatePermissionMutation.isPending ? 'Updating...' : 'Update'}
              type="submit"
              isLoading={updatePermissionMutation?.isPending}
              disabled={updatePermissionMutation?.isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default EditPermission;
